import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Package } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import { uid } from '../lib/uid';
import { BulkStepTemplate } from './magazzino/BulkStepTemplate';
import { BulkStepUnits } from './magazzino/BulkStepUnits';
import { BulkStepQuantita } from './magazzino/BulkStepQuantita';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_VUOTO = {
  descrizione: '', marca: '', modello: '', categoria: '',
  prezzo_acquisto: '', prezzo_vendita: '', codice_prefix: '', note: '',
};

const nuovaUnita = (codice_barre = '', matricola = '') => ({ codice_barre, matricola, _uid: uid() });

/**
 * Dialog per caricare N unità dello stesso prodotto (stesso template)
 * con codici EAN/matricole diverse per ogni unità.
 *
 * Flow:
 * 1. Step 1 (template): descrizione, marca, modello, categoria, prezzo_acquisto, prezzo_vendita
 * 2. Step 2 (unità): tabella righe {codice_barre, matricola}. Lo scanner USB
 *    aggiunge righe automaticamente alternando barcode/matricola per riga.
 * 3. Salva tutti → POST /magazzino/articoli/bulk-carico
 */
export default function BulkCaricoDialog({ open, onOpenChange, isSuperAdmin, onCreated }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('codici'); // 'codici' (scan unità) | 'quantita' (consumabili)
  const [quantita, setQuantita] = useState('');
  const [template, setTemplate] = useState(TEMPLATE_VUOTO);
  const [units, setUnits] = useState([nuovaUnita()]);
  // Quale campo compilare al prossimo scan: 'barcode' | 'matricola' | 'auto'
  const [scanTarget, setScanTarget] = useState('auto');
  const [saving, setSaving] = useState(false);
  const bufferRef = useRef('');
  const bufferTimerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setMode('codici');
      setQuantita('');
      setTemplate(TEMPLATE_VUOTO);
      setUnits([nuovaUnita()]);
      setScanTarget('auto');
    }
  }, [open]);

  const addScannedCode = useCallback((code) => {
    const clean = String(code || '').trim();
    if (!clean) return;
    setUnits((prev) => {
      const rows = prev.map((r) => ({ ...r }));
      // Trova l'ultima riga con almeno un campo vuoto
      let targetIdx = rows.findIndex((r) => !r.codice_barre && !r.matricola);
      if (targetIdx === -1) {
        // tutte le righe sono piene almeno parzialmente → cerca una con un solo campo compilato
        targetIdx = rows.findIndex((r) => !r.codice_barre || !r.matricola);
      }
      if (targetIdx === -1) {
        // aggiungi nuova riga
        rows.push(nuovaUnita());
        targetIdx = rows.length - 1;
      }
      const row = rows[targetIdx];
      if (scanTarget === 'barcode') {
        row.codice_barre = clean;
      } else if (scanTarget === 'matricola') {
        row.matricola = clean;
      } else {
        // auto: se codice_barre vuoto lo metti lì, altrimenti in matricola
        if (!row.codice_barre) row.codice_barre = clean;
        else if (!row.matricola) row.matricola = clean;
        else {
          // riga piena, crea nuova
          rows.push(nuovaUnita(clean));
        }
      }
      return rows;
    });
    toast.success(`+ ${clean}`, { duration: 1000 });
  }, [scanTarget]);

  // Listener globale per scanner USB su step 2 (buffer + Enter)
  useEffect(() => {
    if (!open || step !== 2 || mode !== 'codici') return;
    const handleKey = (e) => {
      const active = document.activeElement;
      // Se sto scrivendo in un input della tabella, lascia normale
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      if (e.key === 'Enter') {
        if (bufferRef.current.length >= 3) {
          addScannedCode(bufferRef.current);
          bufferRef.current = '';
          e.preventDefault();
        }
        return;
      }
      if (e.key.length === 1) {
        bufferRef.current += e.key;
        clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = setTimeout(() => { bufferRef.current = ''; }, 200);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      clearTimeout(bufferTimerRef.current);
    };
  }, [open, step, mode, addScannedCode]);

  const updateRow = (idx, field, value) => {
    setUnits((prev) => {
      const rows = [...prev];
      rows[idx] = { ...rows[idx], [field]: value };
      return rows;
    });
  };

  const removeRow = (idx) => {
    setUnits((prev) => prev.filter((_, i) => i !== idx));
  };

  const addEmptyRow = () => {
    setUnits((prev) => [...prev, nuovaUnita()]);
  };

  const validUnits = units.filter((u) => (u.codice_barre && u.codice_barre.trim()) || (u.matricola && u.matricola.trim()));

  const handleSave = async () => {
    if (!template.descrizione.trim()) {
      toast.error('Descrizione obbligatoria');
      setStep(1);
      return;
    }
    const qta = parseFloat(quantita);
    if (mode === 'quantita') {
      if (!qta || qta <= 0) {
        toast.error('Inserisci una quantità valida');
        return;
      }
    } else if (validUnits.length === 0) {
      toast.error('Aggiungi almeno una unità (con codice EAN o matricola)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        descrizione: template.descrizione,
        marca: template.marca || null,
        modello: template.modello || null,
        categoria: template.categoria || null,
        prezzo_acquisto: parseFloat(template.prezzo_acquisto) || 0,
        prezzo_vendita: parseFloat(template.prezzo_vendita) || 0,
        codice_prefix: template.codice_prefix || null,
        note: template.note || null,
        units: mode === 'quantita' ? [] : validUnits.map((u) => ({
          codice_barre: (u.codice_barre || '').trim() || null,
          matricola: (u.matricola || '').trim() || null,
        })),
        quantita_totale: mode === 'quantita' ? qta : null,
      };
      const { data } = await axios.post(`${API}/magazzino/articoli/bulk-carico`, payload);
      if (mode === 'quantita') {
        if (data.merged) {
          toast.success(`✅ Quantità aggiornata: +${data.quantita_aggiunta} pz (totale: ${data.nuova_quantita})`);
        } else {
          toast.success(`✅ Caricato "${template.descrizione}" — ${data.quantita_aggiunta} pz`);
        }
      } else {
        if (data.created_count > 0) {
          toast.success(`✅ Caricate ${data.created_count} unità in magazzino`);
        }
        if (data.skipped_count > 0) {
          const dettagli = (data.skipped || [])
            .map((s) => `${s.matricola || s.codice_barre || s.codice}: ${s.reason}`)
            .join(' • ');
          toast.warning(`${data.skipped_count} unità saltate — ${dettagli}`, { duration: 8000 });
        }
      }
      onCreated?.(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel carico multiplo'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-carico-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-cyan-500" />
            Carico Multiplo — {step === 1 ? 'Prodotto' : (mode === 'quantita' ? 'Quantità' : `Unità (${validUnits.length})`)}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Definisci prima le caratteristiche comuni (marca, modello, prezzi).'
              : (mode === 'quantita'
                ? 'Indica quanti pezzi stai caricando: verrà creato (o aggiornato) un solo articolo.'
                : 'Ora scansiona una alla volta ogni cassa. Ogni scan aggiunge una riga.')}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <BulkStepTemplate
            mode={mode}
            setMode={setMode}
            template={template}
            setTemplate={setTemplate}
            isSuperAdmin={isSuperAdmin}
          />
        )}

        {step === 2 && mode === 'quantita' && (
          <BulkStepQuantita template={template} quantita={quantita} setQuantita={setQuantita} />
        )}

        {step === 2 && mode === 'codici' && (
          <BulkStepUnits
            units={units}
            validCount={validUnits.length}
            scanTarget={scanTarget}
            setScanTarget={setScanTarget}
            updateRow={updateRow}
            removeRow={removeRow}
            addEmptyRow={addEmptyRow}
          />
        )}

        <DialogFooter className="gap-2">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!template.descrizione.trim()}
                data-testid="bulk-next-btn"
              >
                {mode === 'quantita' ? 'Avanti — Quantità →' : 'Avanti — Scansiona unità →'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>← Modifica prodotto</Button>
              <Button
                onClick={handleSave}
                disabled={saving || (mode === 'quantita' ? !(parseFloat(quantita) > 0) : validUnits.length === 0)}
                data-testid="bulk-save-btn"
              >
                {saving
                  ? 'Salvataggio...'
                  : (mode === 'quantita'
                    ? `Carica ${parseFloat(quantita) > 0 ? parseFloat(quantita) : ''} pz`
                    : `Salva ${validUnits.length} unità`)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
